import { HttpResponseBase } from "@angular/common/http";
import { EnvironmentProviders, inject, provideAppInitializer, Provider } from "@angular/core";
import { Router } from "@angular/router";
import {
  BrowserOptions,
  browserTracingIntegration,
  ErrorEvent,
  ErrorHandlerOptions,
  EventHint,
  replayIntegration,
  TraceService,
} from "@sentry/angular";
import { ApiException } from "src/app/api/client";
import { environment } from "src/environments/environment";
import { appNameVersion } from "../constants/app-version";
import { EnvironmentId } from "../enums/environment-id.enum";
import { appErrorFalsePositiveList } from "../error/app-error-false-positive-list";
import { AppHttpErrorResponse } from "../models/app-http-error-response.model";

const sentryEnvironmentLabelRecord: Record<EnvironmentId, string> = {
  [EnvironmentId.Development]: "Development",
  [EnvironmentId.Test]: "Test",
  [EnvironmentId.Stage]: "Stage",
  [EnvironmentId.Production]: "Production",
};

function beforeSend(event: ErrorEvent, hint: EventHint): PromiseLike<ErrorEvent | null> | ErrorEvent | null {
  // No Internet Connection
  if (typeof navigator.onLine === "boolean" && !navigator.onLine) {
    return null;
  }

  const error: unknown = hint?.originalException;

  if (error && typeof error === "object") {
    const record = <{ name?: unknown; message?: unknown }>error;
    if (appErrorFalsePositiveList.some((x) => record.name?.toString().includes(x) || record.name?.toString().includes(x))) {
      return null;
    }
  }

  if (error && error instanceof Error && (error?.name?.includes("ChunkLoadError") || error?.message?.includes("ChunkLoadError"))) {
    return null;
  }

  // Api Error - Not to be logged by Sentry
  if (error && (error instanceof HttpResponseBase || ApiException.isApiException(error) || AppHttpErrorResponse.isInterceptedByApp(error))) {
    return null;
  }

  // User refused geolocation permission
  if (error && error instanceof GeolocationPositionError && error.code === GeolocationPositionError.PERMISSION_DENIED) {
    return null;
  }

  // Browser doesn't implement es2022, the app's target - Not to be logged by Sentry
  // This is checked on polyfills.ts before adding the "at" polyfill
  if (!Array.prototype.at || document.body?.getAttribute("data-is-legacy") === "true") {
    return null;
  }

  return event;
}

export const sentryBrowserOptions: Readonly<BrowserOptions> = {
  dsn: "https://b15c5b4648c7aa0ac187bcad53aab1a4@crash.siemens.com/271",
  environment: sentryEnvironmentLabelRecord[environment.id],
  release: appNameVersion,
  debug: false,
  beforeSend: (event, hint) => beforeSend(event, hint),
  integrations: [
    // Registers and configures the Tracing integration,
    // which automatically instruments your application to monitor its
    // performance, including custom Angular routing instrumentation
    browserTracingIntegration(),
    // Registers the Replay integration,
    // which automatically captures Session Replays
    replayIntegration(),
  ],

  // Set tracesSampleRate to 1.0 to capture 100%
  // of transactions for performance monitoring.
  // We recommend adjusting this value in production
  tracesSampleRate: environment.id === EnvironmentId.Production ? 0.1 : 1.0,

  // Set `tracePropagationTargets` to control for which URLs distributed tracing should be enabled
  tracePropagationTargets: [],

  // Capture Replay set to 0.1 for 10% of all sessions,
  // plus for 100% of sessions with an error
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
};

export const sentryErrorHandlerOptions: Readonly<ErrorHandlerOptions> = {
  showDialog: false,
};

export const provideSentryTraceService = (): [Provider, EnvironmentProviders] => [
  {
    provide: TraceService,
    deps: [Router],
  },
  provideAppInitializer(() => inject(TraceService) && <void>undefined),
];
